import { FC, useEffect, useState, useRef } from 'react';
import { Icons } from '../../Icons/Icons';
import './MuseumDescription.scss';
import Lottie from 'lottie-react';
import scrollAnim from './Assets/scroll-anim.json';
import useWindowDimensions from '../../../hooks/useWindowDimentions';

interface MuseumDescriptionProps {
  title: string;
  period: string;
  description: JSX.Element;
  isVisible: boolean;
  onClose: () => void;
}

const MuseumDescription: FC<MuseumDescriptionProps> = ({ title, period, description, isVisible, onClose }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [lastTouchY, setLastTouchY] = useState<number | null>(null);
  const descriptionRef = useRef<HTMLDivElement>(null);
  const windowDimensions = useWindowDimensions();

  useEffect(() => {
    const handleScroll = () => {
      if (isHovered && windowDimensions.width >= 768) {
        onClose();
      }
    };

    if (isVisible) {
      window.addEventListener('wheel', handleScroll);
    }

    return () => {
      window.removeEventListener('wheel', handleScroll);
    };
  }, [isVisible, isHovered, onClose, windowDimensions.width]);

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.touches[0].clientY);
    setLastTouchY(e.touches[0].clientY);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!touchStart || !lastTouchY) return;

    const touchY = e.touches[0].clientY;
    const diff = touchStart - touchY;
    const currentDiff = lastTouchY - touchY;

    const descriptionElement = descriptionRef.current;

    if (descriptionElement) {
      const isScrollable = descriptionElement.scrollHeight > descriptionElement.clientHeight;
      const isAtTop = descriptionElement.scrollTop === 0;
      const isAtBottom = Math.abs(
        descriptionElement.scrollHeight - descriptionElement.clientHeight - descriptionElement.scrollTop
      ) < 1;

      if (isScrollable &&
        ((currentDiff > 0 && !isAtBottom) || (currentDiff < 0 && !isAtTop))) {
        e.stopPropagation();
        setLastTouchY(touchY);
        return;
      }

      if ((isAtTop && currentDiff < 0) || (isAtBottom && currentDiff > 0)) {
        e.preventDefault();
      }

      if (diff > 50) {
        onClose();
      }
    }

    setLastTouchY(touchY);
  };

  const handleTouchEnd = () => {
    setTouchStart(null);
    setLastTouchY(null);
  };

  return (
    <div
      className={`museum-description ${!isVisible ? 'hidden' : ''}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <button className="close-button" onClick={onClose}>×</button>
      <h1>{title}</h1>
      <p className="period">{period}</p>
      <div className="description" ref={descriptionRef}>
        {description}
      </div>
      <div className="scroll-hint">
        {windowDimensions.width < 768 ? Icons.SwipeIcon :
          <Lottie
            animationData={scrollAnim}
            loop={true}
            autoplay={true}
            className="scroll-anim"
          />}
        {windowDimensions.width < 768 ? 'swipe up to navigate' : 'SCROLL TO NAVIGATE'}
      </div>
    </div>
  );
};

export default MuseumDescription; 